// MCP 経由の対話専用の useChat インスタンスをホストするカスタムフック。
// 手動チャット側の useChat とは別インスタンスで動作し、履歴は永続化しない
// （useAIAssistantChatHistory を配線しない）。MCP 経由のやりとりは常に modeling モードで動作する。
//
// 【手動チャット側との仕様上の違い】
// MCP 経路と手動チャットは意図的に「別世界」として並走する設計であり、
// 一方の状態がもう一方の UI・入力可否・停止操作に影響しないことが仕様である。
// レビュー時に「片側の状態を両方に反映すべき」と誤指摘しないための備忘。
//
// - 並列実行: MCP と手動チャットは同一 viewModel を同時に編集しうる。
//   ViewModel 側の編集はコマンドパターン + RTDB 楽観的更新で並行を吸収する契約になっており、
//   AIAssistantChat 側で「MCP streaming 中は手動送信を禁止」といったガードは持たない。
//   したがって手動側の `canSend` が MCP の `mcpStatus` を参照しないのは正しい。
// - UI ボタンの参照系: 手動チャットの Send / Stop ボタンは手動 useChat の `status` のみを参照する。
//   MCP は UI に何も描画しない（stream 中でも Stop ボタンは表示されない）。
//   MCP セッションの停止経路は MCP クライアント側（外部プロセス）からの制御が本来の責務であり、
//   AIAssistantChat の UI が担うものではない。
// - unmount 時: AIAssistantChat の cleanup は `session.abort` のみ行い、
//   MCP useChat の stream は useChat 自身が unmount 時に stop する。
//   `useMCPBridgeChat` の返り値に stop を露出しないのは、UI から止める用途がないため。
// - id の分離: 手動側 id は `aichat-${viewModelId}-${sessionId}` で sessionId により reset する。
//   MCP 側 id は `mcp-aichat-${viewModelId}` で意図的に一定（リセットは sendMessageWithFreshHistory 側）。
//   両者の id 衝突回避は prefix (`aichat-` vs `mcp-aichat-`) で担保している。
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import {
  AIAssistantClientToolDefs,
  AIAssistantToolNames,
  extractId,
  type AIAssistantClientSkill,
  type AIAssistantMode,
  type GroupId,
  type ViewId,
  type ViewModelId,
} from 'balus-common';
import {
  getEnabledViewModelAIAssistantSkills,
  subscribeViewModelAIAssistantSkills,
} from '@view-model/application/ai/ViewModelAIAssistantSkillSource';
import { CHAT_STREAMING_THROTTLE_MS } from '../../AIAssistant/chatStreamingThrottle';
import { createViewModelContext } from '../../AIAssistant/createAIViewModelContext';
import { fetchNodeReactionsJSON, fetchViewModelContentsJSON } from '../../AIAssistant/fetchAIViewModelContents';
import type { RunAIEditToolOutput } from '../../AIAssistant/RunAIEditToolOutput';
import type { AIEditToolFailureGuard } from '../../AIAssistant/AIEditToolFailureGuard';
import { AIAssistantSettingsStorage } from '../../AIAssistant/AIAssistantSettingsStorage';
import { buildAISystemMessage } from '../../AIAssistant/buildAISystemMessage';
import { MCPBridgeLogger } from './MCPBridgeLogger';
import type { MCPBridgeChatSession } from './MCPBridgeChatSession';

// MCP セッションは常に modeling モードで動作するため、mode はここで固定する。
// AIAssistantMode 型で受けることで、balus-common 側で mode 名が rename されたときに
// コンパイルエラーとして気付ける。
const MCP_BRIDGE_MODE: AIAssistantMode = 'modeling';

// サーバーが client-executed tool としてクライアントに公開しうるツール名の集合（許可リスト）。
// balus-common の AIAssistantClientToolDefs を単一の真実の源として導出する。
// backend 側（hono/api/clientTools.ts）も同じ定義から inputSchema を導出しているため、
// サーバーに client tool を追加すればこの集合へも自動で反映され、webapp 側の手動同期は不要。
//
// この集合が必要な理由: @mastra/ai-sdk の toAISdkStream は、Agent 側で実行済みのツールの
// tool-input-available チャンクに providerExecuted: true を載せない。そのため AI SDK は
// サーバー実行ツール（getViews 等）の呼び出しでも onToolCall を発火し、
// toolCall.providerExecuted が falsy のまま onToolCall のクライアントツール分岐に到達する。
// onToolCall はこの集合で「クライアントに応答責務がある呼び出しか」を判定する。
//
// 集合の中身は AIAssistantClientToolDefs から導出した client tool 名の union に絞ってから
// ReadonlySet<string> として公開する。
// - Set 生成時に ClientToolName に narrow することで、balus-common 側で name が rename されたり
//   AIAssistantClientToolDefs から外れたりしたときに型エラーとして気付ける
// - has() の呼び出し側は toolCall.toolName: string を受けるため、公開型は string 版のままにする
//   （Set<union>.has は union 引数を要求してしまい string を受けられない）
type ClientToolName = (typeof AIAssistantClientToolDefs)[keyof typeof AIAssistantClientToolDefs]['name'];
const CLIENT_TOOL_NAMES: ReadonlySet<string> = new Set<ClientToolName>(
  Object.values(AIAssistantClientToolDefs).map((def) => def.name)
);

export type UseMCPBridgeChatParams = {
  viewModelId: ViewModelId;
  groupId: GroupId;
  runAIEditTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  runAIPatchViewTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  // 選択中の view の集合。AIAssistantOperationContext は Set<ViewId> を提供するため、
  // ここでも Set のまま受け取り、payload 化するときに配列へ変換する。
  // 配列を要求すると呼び出し側が毎回 [...set] を作る必要があり、spread 忘れると
  // JSON.stringify(Set) === '{}' となって選択情報が silent に落ちる。
  selectedViewIdsRef: MutableRefObject<Set<ViewId>>;
  modelRef: MutableRefObject<string>;
  // MCP セッション。onFinish で確定した各 step の assistant text を appendAssistantMessage で積み、
  // resolvePending 時に request_modeling レスポンスの messages として同梱する。
  session: MCPBridgeChatSession;
  // 手動チャットとは別インスタンスを受け取る（両経路は独立して並走する仕様のため）。
  // parent 側も収束判定で isTripped を読むので、生成は parent が持つ。
  editToolFailureGuard: AIEditToolFailureGuard;
};

type UseMCPBridgeChatResult = {
  // 送信口をこれ 1 本に絞るため、useChat の素の sendMessage は公開しない。
  sendMessageWithFreshHistory: (message: { text: string }) => Promise<unknown>;
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  messages: UIMessage[];
};

export function useMCPBridgeChat(params: UseMCPBridgeChatParams): UseMCPBridgeChatResult {
  const { viewModelId, groupId, selectedViewIdsRef, modelRef } = params;

  // useChat() は引数に渡した関数を初回レンダー時のものを内部で保持し続けるため、
  // parent の再レンダーで最新の runner が届くよう ref 経由で参照する。
  // ref を毎レンダー最新の値で更新することで、body / onToolCall の closure から
  // 常に最新の runner が呼べる。
  const runnersRef = useRef({
    runAIEditTool: params.runAIEditTool,
    runAIPatchViewTool: params.runAIPatchViewTool,
  });
  runnersRef.current = {
    runAIEditTool: params.runAIEditTool,
    runAIPatchViewTool: params.runAIPatchViewTool,
  };

  // session も同じ理由で ref 経由にする。parent が viewModelId 切替時などに
  // 別インスタンスへ差し替えても、onFinish closure が古い session を掴み続けない。
  const sessionRef = useRef(params.session);
  sessionRef.current = params.session;

  // session と同じ理由で ref 経由にする。
  const editToolFailureGuardRef = useRef(params.editToolFailureGuard);
  editToolFailureGuardRef.current = params.editToolFailureGuard;

  // unmount 後に完了した client tool の addToolOutput が、SDK の自動再送で次ターンを始めてしまうのを防ぐ
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // MCPBridgeLogger は同一インスタンスをフックの寿命中ずっと保持する。
  // useRef の初期値に new MCPBridgeLogger() を直接渡すと、初回以外のレンダーでも
  // 引数式が評価されて new が毎回走るため、null 初期値の lazy init 形にする。
  const loggerRef = useRef<MCPBridgeLogger | null>(null);
  if (loggerRef.current === null) {
    loggerRef.current = new MCPBridgeLogger();
  }

  // onFinish で assistantSpeech への重複積みを避けるための状態を 2 本立てで持つ。
  // - loggedTextIndicesRef: 現在処理中の message で、既にログへ積み終えた parts の index の集合
  // - loggedMessageIdRef: 現在処理中の message.id。onFinish 発火時に前回と異なれば
  //   別の message へ移ったと判断して loggedTextIndicesRef をクリアするためのマーカー
  const loggedTextIndicesRef = useRef<Set<number> | null>(null);
  if (loggedTextIndicesRef.current === null) {
    loggedTextIndicesRef.current = new Set();
  }
  const loggedMessageIdRef = useRef<string | null>(null);

  // clientSkills は RTDB 上で他タブから編集される可能性があるため、
  // parent に subscribe を任せると live 更新の配線が抜け落ちやすい。
  // フック内で自前 subscribe を持ち、常に最新値を ref から読めるようにする。
  const clientSkillsRef = useRef<AIAssistantClientSkill[]>([]);
  const clientSkillsReadyRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    clientSkillsRef.current = [];
    let disposed = false;
    let hasSubscriptionValue = false;
    let readyResolved = false;
    let resolveReady: () => void = () => {};
    clientSkillsReadyRef.current = new Promise<void>((resolve) => {
      resolveReady = () => {
        if (readyResolved) return;
        readyResolved = true;
        resolve();
      };
    });

    void getEnabledViewModelAIAssistantSkills(viewModelId)
      .then((skills) => {
        if (!disposed && !hasSubscriptionValue) {
          clientSkillsRef.current = skills;
        }
      })
      .catch((error) => {
        loggerRef.current!.error('Failed to load AI assistant skills', error);
      })
      .finally(resolveReady);

    const unsubscribe = subscribeViewModelAIAssistantSkills(
      viewModelId,
      (skills) => {
        hasSubscriptionValue = true;
        clientSkillsRef.current = skills.filter((skill) => skill.enabled);
        resolveReady();
      },
      (error) => {
        loggerRef.current!.error('Failed to load AI assistant skills', error);
      }
    );

    return () => {
      disposed = true;
      resolveReady();
      unsubscribe();
    };
  }, [viewModelId]);

  const { messages, sendMessage, status, addToolOutput, setMessages } = useChat<UIMessage>({
    // 手動チャット側の `aichat-${viewModelId}-${state.sessionId}` と衝突させないため
    // MCP 用は別プレフィックスにする。
    id: `mcp-aichat-${viewModelId}`,
    // UI は描画しないが、messages 更新はホストの AIAssistantChat を再レンダリングさせる。
    throttle: CHAT_STREAMING_THROTTLE_MS,
    // 止めないと output-error でも SDK が次ターンを再送し、AIが禁止指示に従うかに賭ける形になる。
    sendAutomaticallyWhen: ({ messages: currentMessages }) =>
      !isUnmountedRef.current &&
      !editToolFailureGuardRef.current.isTripped &&
      lastAssistantMessageIsCompleteWithToolCalls({ messages: currentMessages }),
    transport: new DefaultChatTransport({
      api: '/api/aichat',
      headers: {
        'x-application-name': 'balus-webapp',
      },
      body: async () => {
        await clientSkillsReadyRef.current;
        const viewModelContents = await fetchViewModelContentsJSON(viewModelId);
        const modelIds = viewModelContents.models.map((model) => extractId(model.key));
        const stickyNodeReactions = await fetchNodeReactionsJSON(viewModelId, modelIds);
        const viewModelContext = createViewModelContext(viewModelContents, {
          stickyNodeReactions,
        });
        const selectedViewIds = [...selectedViewIdsRef.current];

        const payload: Record<string, unknown> = {
          model: modelRef.current,
          groupId,
          viewModelId,
          // 手動チャット側の modeRef は参照しない。
          // MCP 経由のやりとりが手動チャットのモード切替の影響を受けないようにするため。
          mode: MCP_BRIDGE_MODE,
          // Skill変更はユーザー確認ダイアログを前提とするため、確認画面を持たない
          // MCP経路ではSkill管理ツールをサーバーから公開しない。
          executionSurface: 'mcp',
          viewModelContext,
          selectedViewIds,
          clientSkills: clientSkillsRef.current,
        };

        return payload;
      },
    }),

    onError: (error) => {
      loggerRef.current!.error('useChat error', error);
    },

    onFinish: ({ message, isAbort, isError }) => {
      // message.id が変わったら Set をクリアし、
      // 現在の message に対する重複排除だけを行う。
      if (loggedMessageIdRef.current !== message.id) {
        loggedTextIndicesRef.current!.clear();
        loggedMessageIdRef.current = message.id;
      }

      // AI SDK の onFinish は 1 ターン=1 回ではなく、
      // tool call を挟む複数 step のターンでは各 step の完了ごとに発火する。
      // たとえば「text A → tool 呼び出し → text B」のターンでは、text A まで書き終えた step 完了時と、
      // tool 結果を受けて text B まで書き終えた step 完了時の 2 回発火する。
      //
      // しかも onFinish に渡る message.parts は毎回「そのターンで積まれた全パート」で、差分ではない。
      // 上の例なら 1 回目は [text A]、2 回目は [text A, tool-call, text B]。
      // よって毎回 parts を頭から辿ると、1 回目で積んだ text A を 2 回目でも再度積むことになる。
      message.parts.forEach((part, index) => {
        // parts は text 以外にも tool-call / reasoning / step-start などを含む union 型。
        // ログに積むのは text だけ。
        if (part.type !== 'text') return;

        // TextUIPart.text は型上 string だが、streaming 途中の欠損値や将来のフィールド追加で
        // undefined が来る可能性がある。ここを素通しすると空の assistantSpeech がログに積まれ、
        // 意味のない空発話が MCP ログに残るため、string でない値は弾く。
        if (typeof part.text !== 'string') return;

        // text-start チャンクは受信時点で text: '' の TextUIPart を parts に push する
        // （AI SDK process-ui-message-stream の挙動）。text-delta が来ないまま text-end に至ると
        // text: '' が確定するため、空発話として Logger / session に積まれるのを弾く。
        if (part.text.length === 0) return;

        // 同じ TextUIPart に対する text-delta は既存オブジェクトの text へ += で追記されていく。
        // まだ state === 'done' でない段階の part.text は「そのタイミングまでに届いた部分文字列」で
        // 最終形ではない。ここで積んでしまうと、Set にその index が入り、以降の onFinish で
        // 追記された最終形が silently drop する。state === 'done' を待ってから積むことで、
        // 積むのは常に確定した完全な text だけになる。
        if (part.state !== 'done') return;

        // 同じ index の text は以前の step で積んでいるので除外する。
        // ただしこの実装は、同じ message でも step が進めば parts 配列に
        // 新しい index で新しい text パートが追加される、という AI SDK の挙動に依存している。
        // AI SDK の挙動が変わると、ここが silently drop するため、AI SDK 更新時は再確認が要る。
        if (loggedTextIndicesRef.current!.has(index)) return;

        // 除外 Set へ index を記録し、後続 onFinish で同じ index に対して二重に積まれないようにする。
        loggedTextIndicesRef.current!.add(index);

        // Logger 経路は UI ログ (MCPBridgeLogView) に残す UX 用で、isAbort / isError で
        // 中断されたターンでも「どこまで書いていたか」がユーザーの調査手がかりになるため、
        // 常に積む。除外 index も両経路で共通のカウンタとして更新済み。
        loggerRef.current!.assistantSpeech(part.text);

        // session 経路は request_modeling レスポンスの messages として MCP クライアントに返す契約。
        // isAbort / isError のターンで積んだ text は、同一 session インスタンスで次の start が来て
        // resolve した時に、前ターンの中途 text として messages に混入するリスクがある
        // （現状 MCPBridgeMessageHandler はエラー経路で result を組み立てないため MCP には流れないが、
        // session 内バッファには残る）。よって失敗ターンでは session への append をスキップする。
        if (isAbort || isError) return;
        sessionRef.current.appendAssistantMessage(part.text);
      });
    },

    onToolCall: async ({ toolCall }) => {
      if (toolCall.dynamic) {
        return;
      }

      // サーバ側で解決できていない tool 呼び出しはクライアントで解決する
      if (!toolCall.providerExecuted) {
        // 【addToolOutput は await してはいけない】
        // AI SDK はストリームの各 chunk 処理を SerialJobExecutor のジョブとして直列実行し、
        // tool-input-available chunk のジョブ内で onToolCall を await する
        // （ai@7.0.107: ui/chat.ts の runUpdateMessageJob と ui/process-ui-message-stream.ts）。
        // addToolOutput も同じ executor にジョブを積んで完了を待つ実装のため、onToolCall 内で
        // await すると「chunk ジョブは addToolOutput ジョブの完了を待ち、addToolOutput ジョブは
        // chunk ジョブの完了を待つ」相互待機となり、ストリーム処理全体がデッドロックする。
        // 以降のチャンクが一切処理されなくなり、status='streaming' のままセッションが
        // タイムアウトまでハングする。手動チャット側（AIAssistantChat）と同じく
        // await せず呼びっぱなしにする（積まれたジョブは chunk ジョブ完了後に順次実行される）。
        const handleClientTool = async (
          toolName: string,
          toolCallId: string,
          run: () => Promise<RunAIEditToolOutput>,
          successMessage: string
        ) => {
          let result;
          try {
            result = await run();
          } catch (error) {
            loggerRef.current!.error('Failed to invoke client-executed tool', error);
            addToolOutput({
              state: 'output-error',
              tool: toolName,
              toolCallId,
              errorText: error instanceof Error ? error.message : '何らかの原因で処理の呼び出しに失敗しました',
            });
            return;
          }
          if (result.type === 'error') {
            addToolOutput({
              state: 'output-error',
              tool: toolName,
              toolCallId,
              errorText: result.message,
            });
          } else {
            // note は適用ゲートの advisory やリンク色の置き換え通知で、次ターンの LLM が読んで直す前提の情報。
            // 落とすと指摘が届かないまま完了扱いになる。
            const output = result.message ?? successMessage;
            addToolOutput({
              state: 'output-available',
              tool: toolName,
              toolCallId,
              output: result.note ? `${output}\n${result.note}` : output,
            });
          }
        };

        if (toolCall.toolName === AIAssistantToolNames.addView) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => editToolFailureGuardRef.current.run(() => runnersRef.current.runAIEditTool(toolCall.input)),
            '追加しました。'
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.patchView) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => editToolFailureGuardRef.current.run(() => runnersRef.current.runAIPatchViewTool(toolCall.input)),
            'ビューの内容を差分編集しました。'
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.switchMode) {
          // MCP セッションは常に modeling で動くため switchMode は受け付けない。
          // サーバー側 (hono/api/clientTools.ts) が MCP 経路には switchMode を登録しないため通常ここには来ない。
          // ただし到達した場合に addToolOutput を呼ばないと sendAutomaticallyWhen に渡した
          // lastAssistantMessageIsCompleteWithToolCalls の判定が満たされず、自動再送が発火せず
          // status='streaming' のままセッションがハングするため、結果は必ず返す。
          //
          // output-error を選ぶ理由:
          // switchMode を呼ぶ LLM は通常 chat モードへの切替を望んでいる（既に modeling ならわざわざ呼ばない）。
          // ここで output-available を返すと LLM は「切替に成功した」と誤認して chat モード前提の応答を続ける。
          // 実態は「切替を拒否した」なので、tool-result-error として LLM に伝え、以降 modeling 前提の応答を継続させる。
          // errorText には再呼び出し抑止の明示的な指示も含める（errorText は次ターンの tool-result-error として
          // LLM に渡るため、抑止指示がないと LLM が別パラメータで再試行する余地が残る）。
          //
          // lastAssistantMessageIsCompleteWithToolCalls は output-available と output-error の両方を
          // complete と見なすため、output-error でも自動再送は発火する。
          addToolOutput({
            state: 'output-error',
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            errorText:
              'MCP セッションでは常に modeling モードで動作するため、モードを切り替えられません。以降のターンで switchMode を再度呼び出さないでください。',
          });
          return;
        }

        // AIAssistantClientToolDefs に定義されていない名前は、クライアントに応答責務がないので
        // 何もせず素通りする。主に該当するのはサーバー実行ツール（getViews 等）:
        // providerExecuted が付かないため（CLIENT_TOOL_NAMES のコメント参照）ここに到達するが、
        // 実行と結果配信はサーバー側で完結しており、直後の tool-output-available チャンクで
        // 結果が届く。ここで addToolOutput(output-error) を積むと、本来成功しているサーバー実行
        // ツールに誤ったエラー結果と「再呼び出し禁止」の errorText を与えてしまい、
        // 直後に届くサーバーの実行結果と不整合になる。
        // 拒否リスト（サーバーツール名の列挙）ではなく許可リスト（client tool 定義）で判定するのは、
        // backend 側だけで追加されるサーバーツールに webapp 側のリスト更新が追随できず
        // ストリーム破壊が再発する事故を、構造的に防ぐため。
        // 手動チャット側（AIAssistantChat の onToolCall）は未知ツールへのフォールバック分岐を
        // 持たないことで、結果的に同じ素通り挙動になっている。
        if (!CLIENT_TOOL_NAMES.has(toolCall.toolName)) {
          // 一時的なデバッグ用ログ: サーバー実行ツールの素通りが期待どおり起きているかを
          // ブラウザ console で確認するためのもの。確認が済んだら削除する。
          console.warn(`[MCPBridge] Passed through non-client tool call: ${toolCall.toolName}`);
          return;
        }

        // AIAssistantClientToolDefs に定義済みの client-executed tool なのに webapp 側の分岐が
        // 追随していないケース。addToolOutput を呼ばないと
        // lastAssistantMessageIsCompleteWithToolCalls の判定が満たされず自動再送が発火せず
        // status='streaming' のままセッションがハングするため、output-error で応答して防ぐ
        // （この判定関数は output-available だけでなく output-error も complete と見なすため、
        // output-error を返せば自動再送は発火する）。
        // errorText には再呼び出し抑止の明示的な指示を含める（errorText は tool-result-error
        // として LLM に渡るため、抑止指示がないと LLM は入力ミスと解釈して同じ tool を再度呼び、
        // サーバ側 maxSteps に達するまでトークンを浪費する）。
        loggerRef.current!.warn(`Unsupported client-executed tool was called: ${toolCall.toolName}`);
        addToolOutput({
          state: 'output-error',
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          errorText:
            'このツールは MCP セッションでは実行できません。MCP セッションは人間が介在しない経路のため、以降のターンで再度呼び出さないでください。',
        });
        return;
      }
    },
  });

  // MCP は 1 リクエスト = 1 会話。id 固定でほかにリセット経路がないため、送信直前に履歴を置き直す。
  // setMessages は state.messages を同期的に差し替えるので、直後の sendMessage が送るのは
  // 置き直した内容と新規ユーザー発話だけになる。ターン内の tool ループ（sendAutomaticallyWhen に
  // よる自動再送）は Chat 内部の makeRequest を通るためここを経由せず、1 ターンの文脈は保たれる。
  // カスタムシステムプロンプトは毎回積み直さないと、履歴ごと捨てられて一度も効かない。
  const sendMessageWithFreshHistory = useCallback(
    async (message: { text: string }) => {
      const systemMessage = buildAISystemMessage([AIAssistantSettingsStorage.getCustomSystemPrompt()]);
      setMessages(systemMessage ? [systemMessage] : []);
      // MCP は 1 リクエスト = 1 会話なので、打ち切りもリクエスト単位で解除する。
      editToolFailureGuardRef.current.reset();
      return sendMessage(message);
    },
    [sendMessage, setMessages]
  );

  return { sendMessageWithFreshHistory, status, messages };
}
